import { useState, useMemo, useEffect } from 'react'
import {
  InventoryFiltersProps,
  UseInventoryFiltersLogicReturn,
} from './InventoryFilters.types'
import { FiltersParams } from '../types/InventoryReport.types'

const useInventoryFiltersLogic = ({
  filters,
  onFiltersChange,
  assets,
  initialExpanded = true,
  onExpandChange,
}: InventoryFiltersProps): UseInventoryFiltersLogicReturn => {
  const [expanded, setExpanded] = useState(initialExpanded)
  const [productName, setProductName] = useState<string>(
    filters.productName || ''
  )

  useEffect(() => {
    setProductName(filters.productName || '')
  }, [filters.productName])

  const activeFiltersCount = Object.entries(filters).reduce(
    (count, [key, value]) => {
      if (key === 'open') return count
      if (
        value &&
        ((typeof value === 'string' && value.length > 0) ||
          (Array.isArray(value) && value.length > 0) ||
          (typeof value === 'object' &&
            value !== null &&
            !Array.isArray(value)))
      ) {
        return count + 1
      }
      return count
    },
    0
  )

  const toggleExpanded = () => {
    setExpanded((prev) => !prev)
    onExpandChange?.(!expanded)
  }

  const clearFilter = (key: keyof FiltersParams) => {
    onFiltersChange({ [key]: undefined })
  }

  const handleProductNameChange = (value: string) => {
    setProductName(value)
    onFiltersChange({ productName: value })
  }

  const handleFilterChange = (
    key: keyof FiltersParams,
    values: string[] | null
  ) => {
    if (key === 'selectedCompanies') {
      onFiltersChange({ selectedCompanies: values || undefined })
    } else if (key === 'selectedLocations') {
      onFiltersChange({ selectedLocations: values || undefined })
    } else if (key === 'type') {
      onFiltersChange({
        type: values && values.length > 0 ? (values as any) : undefined,
      })
    } else if (key === 'itemDisposition') {
      onFiltersChange({
        itemDisposition:
          values && values.length > 0 ? (values as any) : undefined,
      })
    } else if (key === 'onHandFilter') {
      onFiltersChange({
        onHandFilter: values && values.length > 0 ? (values as any) : undefined,
      })
    }
  }

  const vendorNames = useMemo(() => {
    return Array.from(
      new Set(assets?.map((asset) => asset.vendorName).filter(Boolean) || [])
    )
  }, [assets])

  const locationNames = useMemo(() => {
    const allLocations =
      assets?.flatMap((asset) => asset.inventoryLocationsNames || []) || []
    return Array.from(new Set(allLocations.filter(Boolean)))
  }, [assets])

  const assetTypeNames = useMemo(() => {
    const validAssetTypes = ['Biologic', 'Hardware', 'Other']
    const allTypes = assets?.map((asset) => asset.type).filter(Boolean) || []
    const validTypes = allTypes.filter((type) =>
      validAssetTypes.includes(type as string)
    )
    return Array.from(new Set(validTypes))
  }, [assets])

  const dispositionNames = useMemo(() => {
    return Array.from(
      new Set(assets?.map((asset) => asset.disposition).filter(Boolean) || [])
    )
  }, [assets])

  const onHandFilterOptions = useMemo(() => {
    return [
      { value: 'Above Par', label: 'Above Par' },
      { value: 'Below Par', label: 'Below Par' },
    ]
  }, [])

  const hasActiveFilters = activeFiltersCount > 0

  return {
    expanded,
    activeFiltersCount,
    hasActiveFilters,
    vendorNames,
    locationNames,
    assetTypeNames,
    dispositionNames,
    onHandFilterOptions,
    productName,
    toggleExpanded,
    clearFilter,
    handleFilterChange,
    handleProductNameChange,
  }
}

export default useInventoryFiltersLogic
